import { useCallback, useEffect, useRef, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";

/** Timbre sintetizado (sin archivos de audio) para la llamada entrante y la saliente. */
const useRingtone = () => {
  const ctxRef = useRef(null);
  const timerRef = useRef(null);

  const stop = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    try { ctxRef.current?.close(); } catch (e) { console.warn('El audio del timbre ya estaba cerrado:', e); }
    ctxRef.current = null;
  }, []);

  const play = useCallback((mode = "in") => {
    stop();
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    ctxRef.current = ctx;
    const beep = () => {
      if (!ctxRef.current) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = mode === "in" ? 660 : 440;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(mode === "in" ? 0.22 : 0.08, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.75);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.8);
    };
    beep();
    timerRef.current = setInterval(beep, mode === "in" ? 1600 : 3000);
  }, [stop]);

  useEffect(() => stop, [stop]);
  return { play, stop };
};

const EMPTY = { muted: false, camOff: false, sharing: false };

/**
 * Llamadas 1 a 1 de voz y video con WebRTC.
 * El servidor solo avisa y pasa la negociación; el audio/video va directo.
 */
export const useCalls = () => {
  const [call, setCall] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const camTrackRef = useRef(null);
  const iceRef = useRef([{ urls: ["stun:stun.l.google.com:19302"] }]);
  const pendingRef = useRef([]);
  const callRef = useRef(null);
  const ring = useRingtone();

  callRef.current = call;

  const sendSignal = useCallback(async (callId, payload) => {
    try { await userApi.post(`/community/calls/${callId}/signal`, { payload }); }
    catch (e) { console.warn('No se pudo enviar la señal (la llamada pudo terminar):', e); }
  }, []);

  const cleanup = useCallback(() => {
    ring.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    camTrackRef.current?.stop?.();
    streamRef.current = null;
    camTrackRef.current = null;
    pendingRef.current = [];
    try { pcRef.current?.close(); } catch (e) { console.warn('La conexión ya estaba cerrada:', e); }
    pcRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setCall(null);
  }, [ring]);

  const getMedia = useCallback(async (kind) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
      video: kind === "video" ? { width: { ideal: 1280 }, facingMode: "user" } : false,
    });
    streamRef.current = stream;
    setLocalStream(stream);
    return stream;
  }, []);

  const signalRef = useRef(null);

  const flushPending = useCallback(async () => {
    const queue = pendingRef.current;
    pendingRef.current = [];
    for (const payload of queue) await signalRef.current?.(payload);
  }, []);

  const handleSignal = useCallback(async (payload) => {
    const pc = pcRef.current;
    const id = callRef.current?.call_id;
    if (!pc || !id) { pendingRef.current.push(payload); return; }
    try {
      if (payload.type === "offer") {
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await sendSignal(id, { type: "answer", sdp: answer });
        await flushPending();
      } else if (payload.type === "answer") {
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        await flushPending();
      } else if (payload.type === "candidate") {
        if (!pc.remoteDescription) { pendingRef.current.push(payload); return; }
        await pc.addIceCandidate(payload.candidate);
      }
    } catch (e) {
      console.warn("Señal de llamada no aplicada:", e);
    }
  }, [sendSignal, flushPending]);

  signalRef.current = handleSignal;

  const buildPeer = useCallback((callId) => {
    const pc = new RTCPeerConnection({ iceServers: iceRef.current });
    pc.onicecandidate = (e) => {
      if (e.candidate) sendSignal(callId, { type: "candidate", candidate: e.candidate.toJSON() });
    };
    pc.ontrack = (e) => setRemoteStream(e.streams[0]);
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") setCall((c) => (c ? { ...c, linked: true } : c));
      if (pc.connectionState === "failed") toast.error("Se perdió la conexión de la llamada");
    };
    setTimeout(() => {
      if (pcRef.current === pc && pc.connectionState !== "connected" && pc.connectionState !== "closed") {
        toast.info("La conexión está tardando. Revisa tu red o prueba con otra red wifi.");
      }
    }, 18000);
    streamRef.current?.getTracks().forEach((t) => pc.addTrack(t, streamRef.current));
    pcRef.current = pc;
    return pc;
  }, [sendSignal]);

  const startCall = useCallback(async (conv, kind) => {
    if (callRef.current) return toast.error("Ya hay una llamada en curso");
    try {
      await getMedia(kind);
    } catch (e) {
      return toast.error("No pudimos usar tu micrófono o cámara. Revisa los permisos del navegador.");
    }
    try {
      const { data } = await userApi.post("/community/calls/start",
        { conv_id: conv.conv_id, kind });
      iceRef.current = data.ice_servers || iceRef.current;
      setCall({ call_id: data.call_id, kind, status: "outgoing",
                peer: conv.people?.[0] || null, conv_id: conv.conv_id, ...EMPTY });
      ring.play("out");
    } catch (e) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setLocalStream(null);
      toast.error(apiError(e, "No se pudo iniciar la llamada"));
    }
  }, [getMedia, ring]);

  const accept = useCallback(async () => {
    const current = callRef.current;
    if (!current) return;
    ring.stop();
    try {
      await getMedia(current.kind);
    } catch (e) {
      toast.error("No pudimos usar tu micrófono o cámara. Revisa los permisos.");
      await userApi.post(`/community/calls/${current.call_id}/reject`).catch(() => {});
      return cleanup();
    }
    try {
      const { data } = await userApi.post(`/community/calls/${current.call_id}/answer`);
      iceRef.current = data.ice_servers || iceRef.current;
      setCall((c) => (c ? { ...c, status: "active" } : c));
      buildPeer(current.call_id);
      await flushPending();
    } catch (e) {
      toast.error(apiError(e, "No se pudo contestar"));
      cleanup();
    }
  }, [getMedia, buildPeer, cleanup, ring, flushPending]);

  const reject = useCallback(async () => {
    const current = callRef.current;
    if (current) await userApi.post(`/community/calls/${current.call_id}/reject`).catch(() => {});
    cleanup();
  }, [cleanup]);

  const hangup = useCallback(async () => {
    const current = callRef.current;
    if (current) {
      await userApi.post(`/community/calls/${current.call_id}/end`, { reason: "hangup" })
        .catch(() => {});
    }
    cleanup();
  }, [cleanup]);

  const toggleMute = useCallback(() => {
    const track = streamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setCall((c) => (c ? { ...c, muted: !track.enabled } : c));
  }, []);

  const toggleCam = useCallback(() => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return toast.error("Esta llamada es solo de voz");
    track.enabled = !track.enabled;
    setCall((c) => (c ? { ...c, camOff: !track.enabled } : c));
  }, []);

  const toggleShare = useCallback(async () => {
    const pc = pcRef.current;
    const sender = pc?.getSenders().find((s) => s.track?.kind === "video");
    if (!pc || !sender) return toast.error("Compartir pantalla solo está en videollamadas");
    if (callRef.current?.sharing) {
      const cam = camTrackRef.current;
      if (cam) { await sender.replaceTrack(cam); camTrackRef.current = null; }
      setCall((c) => (c ? { ...c, sharing: false } : c));
      return;
    }
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const screenTrack = display.getVideoTracks()[0];
      camTrackRef.current = sender.track;
      await sender.replaceTrack(screenTrack);
      setCall((c) => (c ? { ...c, sharing: true } : c));
      screenTrack.onended = async () => {
        const cam = camTrackRef.current;
        if (cam && pcRef.current) await sender.replaceTrack(cam);
        camTrackRef.current = null;
        setCall((c) => (c ? { ...c, sharing: false } : c));
      };
    } catch (e) {
      /* el usuario canceló el selector de pantalla */
    }
  }, []);

  /** Eventos que llegan por el WebSocket de la comunidad. */
  const handleEvent = useCallback((ev) => {
    if (!String(ev.event || "").startsWith("call:")) return;
    if (ev.event === "call:incoming") {
      if (callRef.current) {
        userApi.post(`/community/calls/${ev.call_id}/reject`).catch(() => {});
        return;
      }
      setCall({ call_id: ev.call_id, kind: ev.kind, status: "incoming",
                peer: ev.from, conv_id: ev.conv_id, ...EMPTY });
      ring.play("in");
      return;
    }
    if (ev.call_id !== callRef.current?.call_id) return;
    if (ev.event === "call:accepted") {
      ring.stop();
      setCall((c) => (c ? { ...c, status: "active" } : c));
      const pc = buildPeer(ev.call_id);
      (async () => {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await sendSignal(ev.call_id, { type: "offer", sdp: offer });
        await flushPending();
      })();
      return;
    }
    if (ev.event === "call:signal") { handleSignal(ev.payload); return; }
    if (ev.event === "call:ended") {
      const was = callRef.current?.status;
      cleanup();
      if (ev.status === "rejected") toast.info("Llamada rechazada");
      else if (ev.status === "missed") toast.info(was === "incoming" ? "Llamada perdida" : "Sin respuesta");
      else if (ev.duration_s) toast.info(`Llamada terminada · ${Math.floor(ev.duration_s / 60)}:${String(ev.duration_s % 60).padStart(2, "0")}`);
    }
  }, [buildPeer, sendSignal, handleSignal, cleanup, ring, flushPending]);

  return { call, localStream, remoteStream, startCall, accept, reject, hangup,
           toggleMute, toggleCam, toggleShare, handleEvent };
};

export default useCalls;
